import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../store/authStore';
import { Header } from '../components/Header';
import { SkipLink } from '../components/SkipLink';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Send, ArrowLeft, CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Chat = () => {
  const { sessionId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [lastMessage, setLastMessage] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const messagesContainerRef = useRef(null);

  useEffect(() => {
    fetchSession();
    fetchMessages();
  }, [sessionId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Announce new messages to screen readers
    if (messages.length > 0) {
      const latestMessage = messages[messages.length - 1];
      if (latestMessage.role === 'assistant') {
        setLastMessage(latestMessage.content);
      }
    }
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchSession = async () => {
    try {
      const response = await axios.get(`${API}/sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSession(response.data);
    } catch (error) {
      toast.error('Failed to load session');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/sessions/${sessionId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
      
      // If no messages, send initial greeting
      if (response.data.length === 0) {
        await sendInitialMessage();
      }
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  };

  const sendInitialMessage = async () => {
    try {
      await axios.post(
        `${API}/sessions/${sessionId}/chat`,
        { content: "Hello! I'm ready to fill out this form." },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchMessages();
    } catch (error) {
      console.error('Failed to send initial message:', error);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || sending) return;

    const userMessage = input.trim();
    setInput('');
    setSending(true);

    try {
      const response = await axios.post(
        `${API}/sessions/${sessionId}/chat`,
        { content: userMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      await fetchMessages();
      
      if (response.data.session_status === 'completed') {
        setSession(prev => ({ ...prev, status: 'completed' }));
        toast.success('Form completed! 🎉');
      }
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <SkipLink />
        <Header />
        <main id="main-content" className="flex items-center justify-center min-h-screen">
          <div className="text-center" role="status" aria-live="polite">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1d4ed8] mx-auto" aria-hidden="true"></div>
            <p className="mt-4 text-[#475569]">Loading conversation...</p>
            <span className="sr-only">Loading chat interface</span>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <SkipLink />
      <Header />
      
      {/* Chat Header */}
      <div 
        className="sticky top-16 z-30 bg-white border-b border-[#E2E8F0] px-4 py-4"
        role="banner"
        aria-label="Chat session header"
      >
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              data-testid="back-to-dashboard-button"
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard')}
              className="rounded-full focus:outline-none focus:ring-4 focus:ring-[#1d4ed8] focus:ring-offset-2"
              aria-label="Back to dashboard"
            >
              <ArrowLeft className="h-4 w-4 mr-2" aria-hidden="true" />
              Back
            </Button>
            <div>
              <h1 className="font-bold text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {session?.form_title}
              </h1>
              {session?.status === 'completed' && (
                <div 
                  className="flex items-center space-x-1 text-sm text-green-600"
                  role="status"
                  aria-live="polite"
                >
                  <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                  <span>Completed</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Live region for screen reader announcements */}
      <div 
        role="status" 
        aria-live="polite" 
        aria-atomic="true" 
        className="sr-only"
      >
        {lastMessage && `AI assistant: ${lastMessage}`}
      </div>

      {/* Messages */}
      <main 
        id="main-content"
        className="flex-1 overflow-y-auto px-4 py-8" 
        data-testid="chat-messages-container"
        ref={messagesContainerRef}
        role="log"
        aria-label="Conversation messages"
        aria-live="polite"
        aria-relevant="additions"
      >
        <div className="max-w-2xl mx-auto space-y-6">
          <AnimatePresence>
            {messages.map((message, index) => (
              <motion.div
                key={message.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                data-testid={`message-${message.role}-${index}`}
                role="article"
                aria-label={`${message.role === 'user' ? 'You' : 'AI Assistant'} said`}
              >
                <div
                  className={`max-w-[80%] px-6 py-3 ${
                    message.role === 'user'
                      ? 'chat-user-bubble'
                      : 'chat-ai-bubble'
                  }`}
                >
                  <p className="whitespace-pre-wrap">{message.content}</p>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
          
          {sending && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex justify-start"
              role="status"
              aria-live="polite"
              aria-label="AI is typing"
            >
              <div className="chat-ai-bubble px-6 py-3">
                <div className="flex space-x-2" aria-hidden="true">
                  <div className="w-2 h-2 bg-[#475569] rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-[#475569] rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-[#475569] rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
                <span className="sr-only">AI is typing a response</span>
              </div>
            </motion.div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </main>

      {/* Input */}
      <div 
        className="sticky bottom-0 bg-white border-t border-[#E2E8F0] px-4 py-4"
        role="form"
        aria-label="Message input form"
      >
        <div className="max-w-2xl mx-auto">
          <form 
            onSubmit={handleSend} 
            className="flex items-center space-x-3" 
            data-testid="chat-input-form"
          >
            <label htmlFor="message-input" className="sr-only">
              Type your response to the form question
            </label>
            <Input
              id="message-input"
              ref={inputRef}
              data-testid="message-input"
              type="text"
              placeholder="Type your response..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={sending || session?.status === 'completed'}
              className="flex-1 rounded-full text-base focus:ring-4 focus:ring-[#1d4ed8] focus:ring-offset-2"
              autoFocus
              aria-label="Message input"
              aria-describedby="input-hint"
            />
            <span id="input-hint" className="sr-only">
              Press Enter to send your message
            </span>
            <Button
              data-testid="send-message-button"
              type="submit"
              disabled={!input.trim() || sending || session?.status === 'completed'}
              className="rounded-full bg-[#1d4ed8] hover:bg-[#1e40af] btn-hover-lift px-6 focus:outline-none focus:ring-4 focus:ring-[#1d4ed8] focus:ring-offset-2"
              aria-label={sending ? 'Sending message' : 'Send message'}
            >
              <Send className="h-4 w-4" aria-hidden="true" />
              <span className="sr-only">{sending ? 'Sending' : 'Send'}</span>
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};
